import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";

interface UnlinkTaxPayerFromFulfilmentRequest {
    levyId: string;
    taxPayerWorkspaceId: string;
    year: number;
}

export function useUnlinkTaxPayerFromFulfilment() {
    const { t } = useTranslation();
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders({ method: "POST" });
    const queryClient = useQueryClient();

    const mutation = useMutation<
        boolean,
        Error,
        UnlinkTaxPayerFromFulfilmentRequest
    >({
        mutationFn: async (request) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments/${request.levyId}/years/${request.year}/tax-payers/${request.taxPayerWorkspaceId}/activities/unlink`,
                {
                    headers,
                    method: "POST",
                },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }

            return true;
        },
        onSuccess: () => {
            // NOTE: useGetLinkableTaxPayers and useGetFulfilmentTaxPayers are
            // intentionally NOT invalidated here. The associa/dissocia drawer
            // shows a per-row toggle that lets the user roll back a
            // dissociation before closing the drawer; refetching the list
            // mid-interaction would make the row disappear and break that UX.
            // Those lists refresh naturally on next drawer open (stale by
            // default).
            queryClient.invalidateQueries({ queryKey: ["useGetFulfilments"] });
            queryClient.invalidateQueries({
                queryKey: ["useGetLinkableFulfilments"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
        },
        meta: {
            errorMessage: t("errors.messages.unlinkTaxPayer"),
        },
    });

    return mutation;
}
